import React, { useState, useMemo } from 'react';
import {
  AreaChart, Area, XAxis, YAxis, CartesianGrid,
  Tooltip, ReferenceLine, ResponsiveContainer,
} from 'recharts';
import { TimeRange } from '../../types';
import { useCoinHistory } from '../../hooks/useCoinHistory';
import { useCurrency } from '../../context/CurrencyContext';
import { currencySymbol } from '../../utils/formatters';
import { AlertCircle, TrendingUp, ChevronDown, ChevronUp } from 'lucide-react';
import { CROSSHAIR_STROKE, HorizontalCrosshair } from '../Charts/HorizontalCrosshair';

// ── Types ─────────────────────────────────────────────────────────────────────

export type ChartRange = '7D' | '30D' | '90D' | '1Y';

export const CHART_RANGES: ChartRange[] = ['7D', '30D', '90D', '1Y'];

const TO_HISTORY: Record<ChartRange, TimeRange> = {
  '7D': '1W', '30D': '1M', '90D': '3M', '1Y': '1Y',
};

export interface TierLine {
  price: number;      // in selected currency
  tierIndex: number;  // 0-based
  color: string;      // hex
}

interface Props {
  coinId: string;
  symbol: string;
  tierLines: TierLine[];
  /**
   * Controlled mode (Split Buy): the parent supplies the active range and owns the
   * range buttons + collapse toggle, so this renders the chart body only. Omit it
   * for the uncontrolled, self-contained variant used by Split Sell.
   */
  range?: ChartRange;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

// currencySymbol comes from utils/formatters: "A$" for AUD, matching the tooltip.

function fmtAxisTick(v: number, code: string): string {
  const s = currencySymbol(code);
  if (v >= 1_000_000) return `${s}${(v / 1_000_000).toFixed(1)}M`;
  if (v >= 1_000)     return `${s}${(v / 1_000).toFixed(0)}k`;
  if (v < 1)          return `${s}${v.toFixed(3)}`;
  return `${s}${v.toFixed(0)}`;
}

function fmtLabelPrice(v: number, code: string): string {
  const s = currencySymbol(code);
  if (v >= 1_000_000) return `${s}${(v / 1_000_000).toFixed(2)}M`;
  if (v >= 1_000)     return `${s}${(v / 1_000).toFixed(1)}k`;
  if (v < 1)          return `${s}${v.toFixed(4)}`;
  return `${s}${v.toFixed(0)}`;
}

// Custom SVG label for ReferenceLine — renders right-aligned inside the chart
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function TierLabel({ viewBox, value, fill }: any) {
  if (!viewBox) return null;
  const { x, y, width } = viewBox;
  const labelX = x + (width ?? 0) - 4;
  return (
    <text
      x={labelX}
      y={y - 4}
      fill={fill}
      fontSize={10}
      fontWeight={600}
      textAnchor="end"
      dominantBaseline="auto"
    >
      {value}
    </text>
  );
}

// ── Inner chart (coinId guaranteed non-empty; range supplied by the header) ────

function ChartInner({ coinId, tierLines, range }: { coinId: string; tierLines: TierLine[]; range: ChartRange }) {
  const { rate, currency } = useCurrency();
  const { points, loading, error } = useCoinHistory(coinId, TO_HISTORY[range]);

  const data = useMemo(
    () =>
      points.map(p => ({
        ts:    p.timestamp,
        price: p.price * rate,
        date:  new Date(p.timestamp).toLocaleDateString('en-US', {
          month: 'short',
          day:   'numeric',
        }),
      })),
    [points, rate]
  );

  // Y-axis domain — extend to fit all tier lines
  const chartPrices    = data.map(d => d.price);
  const validTierPrices = tierLines.map(t => t.price).filter(p => p > 0);
  const allPrices      = [...chartPrices, ...validTierPrices];
  const domainMin      = allPrices.length ? Math.min(...allPrices) * 0.97 : 0;
  const domainMax      = allPrices.length ? Math.max(...allPrices) * 1.03 : 1;

  const isPositive = data.length >= 2 && data[data.length - 1].price >= data[0].price;
  const lineColor  = isPositive ? '#10b981' : '#ef4444';

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const renderTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const { ts, price } = payload[0].payload as { ts: number; price: number };
    const priceStr = new Intl.NumberFormat('en-US', {
      style:                 'currency',
      currency:              currency.code,
      minimumFractionDigits: price < 1 ? 4 : 2,
      maximumFractionDigits: price < 1 ? 4 : 2,
    }).format(price);
    const dateStr = new Date(ts).toLocaleDateString('en-US', {
      weekday: range === '7D' ? 'short' : undefined,
      month:   'short',
      day:     'numeric',
      year:    range === '1Y' ? 'numeric' : undefined,
    });
    return (
      <div className="bg-slate-800 border border-slate-700 rounded-xl px-3 py-2.5 shadow-xl">
        <p className="text-xs text-slate-400 mb-1">{dateStr}</p>
        <p className="text-sm font-bold text-slate-100">{priceStr}</p>
      </div>
    );
  };

  if (error && !loading) {
    return (
      <div className="flex items-center justify-center gap-2 h-44 rounded-xl border border-red-500/20 bg-red-500/5">
        <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
        <p className="text-xs text-red-400">{error}</p>
      </div>
    );
  }

  return (
    <div className={loading && data.length === 0 ? 'opacity-40 pointer-events-none' : ''}>
      <ResponsiveContainer width="100%" height={200}>
        <AreaChart data={data} margin={{ top: 14, right: 8, left: 0, bottom: 0 }}>
          <defs>
            <linearGradient id="sbpAreaGrad" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%"  stopColor={lineColor} stopOpacity={0.2} />
              <stop offset="95%" stopColor={lineColor} stopOpacity={0} />
            </linearGradient>
          </defs>

          <CartesianGrid strokeDasharray="3 3" stroke="var(--chart-grid)" vertical={false} />

          <XAxis
            dataKey="date"
            tick={{ fill: 'var(--chart-tick)', fontSize: 10 }}
            axisLine={false}
            tickLine={false}
            interval="preserveStartEnd"
          />

          <YAxis
            tick={{ fill: 'var(--chart-tick)', fontSize: 10 }}
            axisLine={false}
            tickLine={false}
            tickFormatter={v => fmtAxisTick(v, currency.code)}
            domain={[domainMin, domainMax]}
            width={58}
          />

          <Tooltip content={renderTooltip} cursor={CROSSHAIR_STROKE} />

          <Area
            type="monotone"
            dataKey="price"
            stroke={lineColor}
            strokeWidth={2}
            fill="url(#sbpAreaGrad)"
            dot={false}
            activeDot={{ fill: lineColor, strokeWidth: 0, r: 4 }}
          />

          {/* The axis's own formatter: coin prices need their decimals (A$1.40),
              which a compact money label would round away. */}
          <HorizontalCrosshair dataKey="price" format={(v) => fmtAxisTick(v, currency.code)} fontSize={10} />

          {tierLines.map(line =>
            line.price > 0 ? (
              <ReferenceLine
                key={line.tierIndex}
                y={line.price}
                stroke={line.color}
                strokeDasharray="5 3"
                strokeWidth={1.5}
                label={
                  <TierLabel
                    value={`T${line.tierIndex + 1} - ${fmtLabelPrice(line.price, currency.code)}`}
                    fill={line.color}
                  />
                }
              />
            ) : null
          )}
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
}

// ── Placeholder until a coin is selected ──────────────────────────────────────

function ChartPlaceholder() {
  return (
    <div className="flex flex-col items-center justify-center h-32 rounded-xl border border-slate-700/50 bg-slate-800/30">
      <TrendingUp className="w-6 h-6 text-slate-700 mb-2" />
      <p className="text-sm text-slate-500">Select an asset to view chart</p>
    </div>
  );
}

// ── Uncontrolled variant (Split Sell) — self-contained header + collapse toggle ─

function UncontrolledChart({ coinId, tierLines }: { coinId: string; tierLines: TierLine[] }) {
  const [expanded, setExpanded] = useState(false);
  const [range, setRange] = useState<ChartRange>('90D');
  const hasCoin = !!coinId;

  return (
    <div className="space-y-2">
      <div className="flex items-center gap-2">
        <span className="text-xs font-medium text-slate-400 uppercase tracking-wider">Price chart</span>
        <div className="ml-auto flex items-center gap-1">
          {hasCoin && CHART_RANGES.map(r => (
            <button
              key={r}
              type="button"
              onClick={() => setRange(r)}
              className={`px-2.5 py-1 rounded-lg text-xs font-medium transition-colors ${
                range === r
                  ? 'bg-slate-700 text-slate-100 border border-slate-600'
                  : 'text-slate-500 hover:text-slate-300 hover:bg-slate-800/60'
              }`}
            >
              {r}
            </button>
          ))}
          <button
            type="button"
            onClick={() => setExpanded(v => !v)}
            className="ml-1 p-1 rounded-lg text-slate-500 hover:text-slate-200 hover:bg-slate-800/60 transition-colors"
            aria-label={expanded ? 'Collapse chart' : 'Expand chart'}
            aria-expanded={expanded}
          >
            {expanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
          </button>
        </div>
      </div>
      {expanded && (hasCoin ? <ChartInner coinId={coinId} tierLines={tierLines} range={range} /> : <ChartPlaceholder />)}
    </div>
  );
}

// ── Outer shell — controlled (Split Buy, body only) or uncontrolled (Split Sell) ─

export function SplitBuyChart({ coinId, tierLines, range }: Props) {
  if (range !== undefined) {
    // Controlled: parent owns range + collapse, so render just the body.
    return coinId ? <ChartInner coinId={coinId} tierLines={tierLines} range={range} /> : <ChartPlaceholder />;
  }
  return <UncontrolledChart coinId={coinId} tierLines={tierLines} />;
}
